import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Calculate, TrendingUp, TrendingDown, Info } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SummaryCard = styled(Card)(({ theme, regime }) => ({
  backgroundColor: regime === 'new' ? theme.palette.success.light : theme.palette.secondary.light,
  marginBottom: theme.spacing(3)
}));

const TaxLiabilityCalculator = () => {
  const [employeeId, setEmployeeId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [useInitialDeclarations, setUseInitialDeclarations] = useState(true);
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const currentYear = new Date().getFullYear();
  
  const financialYearOptions = [
    `${currentYear}-${currentYear+1}`,
    `${currentYear - 1}-${currentYear}`,
    `${currentYear - 2}-${currentYear - 1}`,
    `${currentYear - 3}-${currentYear - 2}`,
    `${currentYear - 4}-${currentYear - 3}`
  ];
  const [selectedYear, setSelectedYear] = useState(`${currentYear}-${currentYear+1}`);

  // Months list
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleEmployeeIdChange = (event) => {
    setEmployeeId(event.target.value);
    setTaxData(null);
    setError(null);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setTaxData(null);
    setError(null);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setTaxData(null);
    setError(null);
  };

  const handleDeclarationToggle = () => {
    setUseInitialDeclarations(!useInitialDeclarations);
    setTaxData(null);
    setError(null);
  };

  const calculateTaxLiability = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    if (!employeeId || !selectedYear) {
      setError('Please enter both an employee ID and select a financial year.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build URL with month if selected
      let apiUrl = `${API_URL}/api/payrolltax/calculate/${employeeId}/${selectedYear}`;
      
      if (selectedMonth) {
        apiUrl += `/${selectedMonth}`;
      }
      
      apiUrl += `?useInitialDeclarations=${useInitialDeclarations}`;

      const response = await fetch(apiUrl, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! Status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            errorMsg = errorData.message;
          }
        } catch {
          errorMsg = response.statusText || errorMsg;
        }
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const data = await response.json();
      setTaxData(data.data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to calculate tax liability: ${err.message}`);
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // ========== TAX COMPARISON TABLE ==========
  const renderTaxComparisonTable = () => {
    if (!taxData) return null;
    
    const sheet = taxData.tax_computation_sheet?.particulars;
    if (!sheet) return null;
    
    return (
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table aria-label="tax comparison table">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><Typography variant="subtitle2">Particulars</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle2">New Regime</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle2">Old Regime</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(sheet).map(([key, values]) => {
              if (key === 'prepaid_tax') return null;
              
              const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              return (
                <TableRow key={key} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                  <TableCell component="th" scope="row">
                    {formattedKey}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(values.new_regime)}</TableCell>
                  <TableCell align="right">{formatCurrency(values.old_regime)}</TableCell>
                </TableRow>
              );
            })}
            
            {sheet.prepaid_tax && (
              <>
                <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                  <TableCell component="th" scope="row">Tax Recovered (TDS)</TableCell>
                  <TableCell align="right">{formatCurrency(sheet.prepaid_tax.tds_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(sheet.prepaid_tax.tds_amount)}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                  <TableCell component="th" scope="row">Tax Recovered (TCS)</TableCell>
                  <TableCell align="right">{formatCurrency(sheet.prepaid_tax.tcs_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(sheet.prepaid_tax.tcs_amount)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ========== TAX DIFFERENCE SUMMARY ==========
  const renderTaxSummary = () => {
    if (!taxData) return null;
    
    const taxDiff = taxData.tax_computation_sheet?.tax_difference;
    if (!taxDiff) return null;
    
    const isBetterNewRegime = taxDiff.better_regime === "New Regime";
    
    return (
      <SummaryCard regime={isBetterNewRegime ? 'new' : 'old'} elevation={3}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            Tax Liability Summary
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" color="primary">
                  {taxDiff.better_regime} is better
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {taxDiff.will_increase ? (
                  <TrendingDown color="success" fontSize="large" />
                ) : (
                  <TrendingUp color="error" fontSize="large" />
                )}
                <Typography variant="h6">
                  You {taxDiff.will_increase ? 'save' : 'pay extra'} {formatCurrency(taxDiff.amount)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Monthly Tax (New Regime)
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(taxData.tax_computation_sheet.particulars?.tax_per_month?.new_regime || 0)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Monthly Tax (Old Regime)
                </Typography>
                <Typography variant="h6" color="secondary">
                  {formatCurrency(taxData.tax_computation_sheet.particulars?.tax_per_month?.old_regime || 0)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </SummaryCard>
    );
  };

  // ========== PAYROLL STATUS ==========
  const renderPayrollStatus = () => {
    if (!taxData) return null;
    
    const { payroll_status, payroll_message, monthly_adjustments, month, year } = taxData;
    
    if (!payroll_status) return null;
    
    let statusColor = 'default';
    let statusLabel = '';
    
    if (payroll_status === 'processed') {
      statusColor = 'success';
      statusLabel = '✅ Payroll Processed';
    } else if (payroll_status === 'not_processed') {
      statusColor = 'warning';
      statusLabel = '⚠️ Payroll Not Processed';
    } else if (payroll_status === 'no_employee_data') {
      statusColor = 'error';
      statusLabel = '⚠️ No Employee Data';
    }
    
    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Chip 
              label={statusLabel} 
              color={statusColor} 
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              {payroll_message}
            </Typography>
          </Grid>
          {month && year && (
            <Grid item>
              <Chip 
                label={`${month} ${year}`} 
                size="small" 
                variant="outlined"
              />
            </Grid>
          )}
        </Grid>
        
        {monthly_adjustments && payroll_status === 'processed' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Monthly Adjustments:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  LOP Days: <strong>{monthly_adjustments.lop_days}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  LOP Deduction: <strong>{formatCurrency(monthly_adjustments.lop_deduction)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  OT Hours: <strong>{monthly_adjustments.ot_hours}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  OT Amount: <strong>{formatCurrency(monthly_adjustments.ot_amount)}</strong>
                </Typography>
              </Grid>
              {monthly_adjustments.variable_pay_this_month > 0 && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Variable Pay: <strong>{formatCurrency(monthly_adjustments.variable_pay_this_month)}</strong>
                  </Typography>
                </Grid>
              )}
              {monthly_adjustments.joining_bonus_this_month > 0 && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Joining Bonus: <strong>{formatCurrency(monthly_adjustments.joining_bonus_this_month)}</strong>
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Current Month Gross: <strong>{formatCurrency(monthly_adjustments.current_month_gross)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Already Earned: <strong>{formatCurrency(monthly_adjustments.already_earned)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Months Paid: <strong>{monthly_adjustments.months_paid}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Remaining Months: <strong>{monthly_adjustments.remaining_months}</strong>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    );
  };

  // ========== PROOF VALIDATION SUMMARY ==========
  const renderProofSummary = () => {
    if (!taxData) return null;
    
    const proofSummary = taxData.tax_computation_sheet?.proof_validation_summary;
    if (!proofSummary) return null;
    
    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
        <Typography variant="subtitle2" gutterBottom>
          Proof Validation Summary
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">
              HRA Status: <strong>{proofSummary.hra?.proof_status || 'N/A'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">
              Total Exemption Used: <strong>{formatCurrency(proofSummary.total_exemption_used || 0)}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">
              Validation Mode: <strong>{proofSummary.validation_mode || 'N/A'}</strong>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold"> 
          Tax Liability Calculator
        </Typography>
        
        {/* ========== INPUT SECTION ========== */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                id="employee-id"
                label="Employee ID"
                variant="outlined"
                fullWidth
                required
                value={employeeId}
                onChange={handleEmployeeIdChange}
                placeholder="Enter employee ID"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                id="financial-year"
                select
                required
                label="Financial Year"
                value={selectedYear}
                onChange={handleYearChange}
                fullWidth
                variant="outlined"
                size="small"
              >
                {financialYearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            {/* ========== MONTH DROPDOWN (NEW) ========== */}
            <Grid item xs={12} md={3}>
              <TextField
                id="month"
                select
                required
                label="Month"
                value={selectedMonth}
                onChange={handleMonthChange}
                fullWidth
                variant="outlined"
                size="small"
              >
                <MenuItem value="">All Months (Annual)</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useInitialDeclarations}
                    onChange={handleDeclarationToggle}
                    color="primary"
                    size="small"
                  />
                }
                label="Use Initial Declarations"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={calculateTaxLiability}
                disabled={loading || !employeeId || !selectedYear || !selectedMonth}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
                size="large"
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Calculating...' : 'Calculate Tax Liability'}
              </Button>
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
        
        {/* ========== RESULTS SECTION ========== */}
        {taxData && (
          <Box>
            {/* Payroll Status */}
            {renderPayrollStatus()}
            
            {/* Proof Summary */}
            {/* {renderProofSummary()} */}
            
            {/* Tax Summary */}
            {/* {renderTaxSummary()} */}
            
            {/* Tax Comparison Table */}
            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
              Detailed Tax Computation
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {renderTaxComparisonTable()}
            
            {/* Calculation Mode */}
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="textSecondary">
                Calculation Mode: <strong>{taxData.tax_computation_sheet?.calculation_mode || 'N/A'}</strong>
                {' | '}
                Financial Year: <strong>{taxData.financial_year || 'N/A'}</strong>
                {taxData.month && (
                  <>
                    {' | '}
                    Month: <strong>{taxData.month}</strong>
                  </>
                )}
                {taxData.year && (
                  <>
                    {' | '}
                    Year: <strong>{taxData.year}</strong>
                  </>
                )}
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TaxLiabilityCalculator;